import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AccountingService } from '../../../services/accounting.service';
import { UserModel } from '../../../models/user.model';
import { AppState } from '../../../state/appstate';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'app-my-giving',
  standalone: true,
  imports: [
    CommonModule,
    BtnBackComponent,
    BtnActionComponent,
  ],
  templateUrl: './my-giving.component.html',
})
export class MyGivingComponent implements OnInit, OnDestroy {
  private readonly store: Store<AppState> = inject(Store<AppState>);
  private readonly afs: AngularFirestore = inject(AngularFirestore);
  private readonly accountingService: AccountingService = inject(AccountingService);
  private readonly destroy$ = new Subject<void>();

  user: UserModel | null = null;

  allUserPayments: any[] = [];
  threeMonthPayments: any[] = [];
  visiblePayments: any[] = [];

  groupNames: { [groupId: string]: string } = {};
  paymentsLoading = false;
  displayedLimit = 10;
  isShowingOlderBeyond3Months = false;

  totalGivenInWindow = 0;
  primaryCurrency = 'USD';

  private paymentsSubscription: any = null;

  ngOnInit(): void {
    this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.user = state.user;
      if (this.user?.uid) {
        this.loadGivingHistory(this.user.uid);
      }
    });
  }

  loadGivingHistory(uid: string) {
    if (this.paymentsSubscription) {
      this.paymentsSubscription.unsubscribe();
    }
    this.paymentsLoading = true;

    // Fetch payments sorted by timestamp desc
    this.paymentsSubscription = this.accountingService.getAllUserPayments(uid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (payments) => {
          this.allUserPayments = payments || [];
          this.filterThreeMonths();
          this.paymentsLoading = false;
          this.loadGroupNames(payments);
        },
        error: (err) => {
          console.error('Error fetching user giving history:', err);
          this.paymentsLoading = false;
        }
      });
  }

  filterThreeMonths() {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    this.threeMonthPayments = this.allUserPayments.filter((p) => {
      const date = this.getPaymentDate(p);
      return date ? date >= threeMonthsAgo : true;
    });

    this.updateVisiblePayments();
  }

  updateVisiblePayments() {
    const sourceList = this.isShowingOlderBeyond3Months
      ? this.allUserPayments
      : this.threeMonthPayments;

    this.visiblePayments = sourceList.slice(0, this.displayedLimit);

    // Calculate total given in current view
    this.totalGivenInWindow = this.threeMonthPayments.reduce((acc, curr) => {
      if (curr.status === 'succeeded' || !curr.status) {
        return acc + (Number(curr.amount) || 0);
      }
      return acc;
    }, 0);

    if (this.threeMonthPayments.length > 0 && this.threeMonthPayments[0].currency) {
      this.primaryCurrency = this.threeMonthPayments[0].currency;
    }
  }

  showMore() {
    const currentList = this.isShowingOlderBeyond3Months
      ? this.allUserPayments
      : this.threeMonthPayments;

    if (this.displayedLimit < currentList.length) {
      this.displayedLimit += 10;
    } else if (!this.isShowingOlderBeyond3Months && this.allUserPayments.length > this.threeMonthPayments.length) {
      // If we exhausted 3-month records but have older records, enable showing older
      this.isShowingOlderBeyond3Months = true;
      this.displayedLimit += 10;
    }

    this.updateVisiblePayments();
  }

  hasMoreItems(): boolean {
    if (!this.isShowingOlderBeyond3Months) {
      if (this.displayedLimit < this.threeMonthPayments.length) {
        return true;
      }
      return this.allUserPayments.length > this.threeMonthPayments.length;
    }
    return this.displayedLimit < this.allUserPayments.length;
  }

  getPaymentDate(payment: any): Date | null {
    if (!payment?.timestamp) return null;
    if (payment.timestamp?.seconds) {
      return new Date(payment.timestamp.seconds * 1000);
    }
    if (payment.timestamp?.toDate) {
      return payment.timestamp.toDate();
    }
    return new Date(payment.timestamp);
  }

  loadGroupNames(payments: any[]) {
    const groupIds = [...new Set(payments.map((p) => p.groupId))];
    groupIds.forEach((groupId) => {
      if (groupId && !this.groupNames[groupId]) {
        this.afs
          .collection('groups')
          .doc(groupId)
          .get()
          .subscribe((docSnap) => {
            if (docSnap.exists) {
              const data = docSnap.data() as any;
              this.groupNames[groupId] = data?.name || 'Unknown Group';
            } else {
              this.groupNames[groupId] = 'Unknown Group';
            }
          });
      }
    });
  }

  refresh() {
    if (this.user?.uid) {
      this.loadGivingHistory(this.user.uid);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
